import { useState } from 'react'
import {X, Star, Loader2, Building2, CheckCircle2, AlertCircle, Clock, MessageSquare, CalendarClock, CheckCheck} from 'lucide-react'
import { COMPANY_EVALUATION_ASPECTS, computeAverage } from '@/lib/evaluationAspects'
import { formatPeriodLabel } from '@/lib/periodUtils'
import { useEvaluations } from '@/hooks/useEvaluations'
import type { WorkLink } from '@/hooks/useLinks'

interface CompanyEvaluationModalProps {
  link: WorkLink
  collaboratorUserId: string
  collaboratorName: string
  /**
   * Periodos (YYYY-MM) pendientes de evaluación para esta vinculación,
   * calculados por el llamador entre el inicio del vínculo y el mes actual
   * (o el mes de finalización), excluyendo los ya evaluados.
   */
  availablePeriods: string[]
  onClose: () => void
  onSuccess?: () => void
}

/**
 * Dado un vínculo finalizado, retorna `true` si aún está dentro de la
 * ventana de 24 h desde que fue finalizado (updatedAt) para evaluar.
 */
function withinGraceWindow(link: WorkLink): boolean {
  if (link.status !== 'finalizada') return true // activo siempre puede
  const ref = link.endDate || link.updatedAt
  if (!ref) return false
  const diffMs = Date.now() - new Date(ref).getTime()
  return diffMs <= 24 * 60 * 60 * 1000
}

function ScoreSelector({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`h-8 w-8 rounded-lg text-sm font-semibold transition-all ${
            value === n
              ? n >= 8
                ? 'bg-emerald-500 text-white shadow-sm'
                : n >= 6
                ? 'bg-amber-400 text-slate-900 shadow-sm'
                : 'bg-red-500 text-white shadow-sm'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  )
}

export default function CompanyEvaluationModal({
  link,
  collaboratorUserId,
  collaboratorName,
  availablePeriods,
  onClose,
  onSuccess,
}: CompanyEvaluationModalProps) {
  const { createEvaluation } = useEvaluations()

  // Periodos pendientes ordenados cronológicamente (más antiguo primero),
  // para invitar a completar primero los meses que quedaron atrasados.
  const sortedPeriods = [...availablePeriods].sort()
  const graceOk = withinGraceWindow(link)

  // ── Estado del formulario ───────────────────────────────────────────────
  const [period, setPeriod] = useState<string>(sortedPeriods[0] || '')
  const initialScores: Record<string, number> = {}
  COMPANY_EVALUATION_ASPECTS.forEach((a) => { initialScores[a.key] = 0 })

  const [scores, setScores] = useState<Record<string, number>>(initialScores)
  // Notas de mejora por área (solo se muestran / piden cuando el score ≤ 5)
  const [improvementNotes, setImprovementNotes] = useState<Record<string, string>>({})
  const [comments, setComments] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const allScored = COMPANY_EVALUATION_ASPECTS.every((a) => scores[a.key] > 0)
  const average = allScored ? computeAverage(scores, COMPANY_EVALUATION_ASPECTS) : 0
  const canEvaluate = !!period && graceOk

  const handleScore = (key: string, value: number) => {
    setScores((prev) => ({ ...prev, [key]: value }))
    // Si sube de 5, limpia la nota de mejora
    if (value > 5) {
      setImprovementNotes((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }

  const handleSubmit = async () => {
    if (!allScored || !canEvaluate) return
    setSubmitting(true)
    try {
      // Construimos el campo observations agregando las notas por área
      const noteLines = COMPANY_EVALUATION_ASPECTS
        .filter((a) => scores[a.key] <= 5 && improvementNotes[a.key]?.trim())
        .map((a) => `• ${a.label}: ${improvementNotes[a.key].trim()}`)
      const observations = noteLines.length > 0 ? noteLines.join('\n') : undefined

      await createEvaluation({
        companyUserId: link.companyUserId,
        companyName: link.companyName,
        collaboratorUserId,
        collaboratorName,
        linkId: link._id,
        period,
        scores,
        average,
        evaluationDirection: 'colaborador_a_empresa',
        comments: comments.trim() || undefined,
        observations,
        creator: collaboratorUserId,
      })
      setDone(true)
      onSuccess?.()
    } catch (e) {
      console.error('Error al guardar evaluacion de empresa:', e)
      window.alert('No se pudo guardar la evaluación. Inténtalo de nuevo.')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Pantalla de bloqueo (sin periodos pendientes) ───────────────────────
  if (sortedPeriods.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 mx-auto mb-4">
            <CheckCheck size={28} className="text-emerald-500" />
          </div>
          <p className="text-lg font-bold text-slate-900">Estás al día</p>
          <p className="mt-2 text-sm text-slate-500">
            Ya registraste tu evaluación de todos los meses disponibles para <strong>{link.companyName || 'esta empresa'}</strong>.
          </p>
          <button
            onClick={onClose}
            className="mt-6 rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Entendido
          </button>
        </div>
      </div>
    )
  }

  // ── Pantalla de bloqueo (fuera de ventana de 24h) ───────────────────────
  if (!graceOk && link.status === 'finalizada') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 mx-auto mb-4">
            <Clock size={28} className="text-slate-500" />
          </div>
          <p className="text-lg font-bold text-slate-900">Plazo expirado</p>
          <p className="mt-2 text-sm text-slate-500">
            Una vez finalizada la vinculación, tanto tú como la empresa tienen <strong>24 horas</strong> para
            completar las evaluaciones pendientes. El plazo para esta vinculación ya venció.
          </p>
          <button
            onClick={onClose}
            className="mt-6 rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Cerrar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-500">
              <Building2 size={20} />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Evaluar empresa</h3>
              <p className="text-xs text-slate-500">{link.companyName || 'Empresa'} · {formatPeriodLabel(period)}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        {/* Aviso vínculo finalizado dentro de ventana */}
        {link.status === 'finalizada' && !done && (
          <div className="mx-6 mt-4 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex-shrink-0">
            <Clock size={15} className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">
              Tu vinculación con esta empresa ha finalizado. Tienes <strong>24 horas</strong> desde
              la desvinculación para completar las evaluaciones pendientes.
            </p>
          </div>
        )}

        {done ? (
          <div className="flex flex-col items-center justify-center gap-4 py-16 px-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle2 size={32} className="text-emerald-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">¡Evaluación enviada!</p>
              <p className="mt-1 text-sm text-slate-500">
                Tu calificación de <strong>{link.companyName}</strong> correspondiente a <strong>{formatPeriodLabel(period)}</strong> ha sido registrada.<br />
                Contribuye a construir un ecosistema laboral más transparente.
              </p>
            </div>
            <button
              onClick={onClose}
              className="mt-2 rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <>
            {/* Selector de periodo pendiente */}
            <div className="mx-6 mt-4 flex-shrink-0">
              <label className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                <CalendarClock size={14} className="text-slate-400" />
                Periodo a evaluar
              </label>
              {sortedPeriods.length > 1 ? (
                <>
                  <select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-medium text-slate-800 focus:border-amber-400 focus:outline-none"
                  >
                    {sortedPeriods.map((p) => (
                      <option key={p} value={p}>{formatPeriodLabel(p)}</option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-amber-600">
                    Tienes {sortedPeriods.length} evaluaciones pendientes para esta empresa. Selecciona el mes que deseas registrar ahora.
                  </p>
                </>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-800">
                  {formatPeriodLabel(period)}
                </div>
              )}
            </div>

            {/* Promedio en tiempo real */}
            {allScored && (
              <div className="mx-6 mt-4 flex items-center gap-3 rounded-xl bg-amber-50 px-4 py-3 flex-shrink-0">
                <Star size={16} className="text-amber-500" />
                <span className="text-sm font-medium text-amber-800">
                  Promedio actual:&nbsp;
                  <strong className={average >= 8 ? 'text-emerald-700' : average >= 6 ? 'text-amber-700' : 'text-red-600'}>
                    {average.toFixed(1)} / 10
                  </strong>
                </span>
              </div>
            )}

            {/* Aspectos */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {COMPANY_EVALUATION_ASPECTS.map((aspect) => {
                const score = scores[aspect.key]
                const isOpportunity = score > 0 && score <= 5
                return (
                  <div key={aspect.key} className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{aspect.label}</p>
                        <p className="text-xs text-slate-400">{aspect.description}</p>
                      </div>
                      {score > 0 && (
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${
                          score >= 8 ? 'bg-emerald-50 text-emerald-700'
                          : score >= 6 ? 'bg-amber-50 text-amber-700'
                          : 'bg-red-50 text-red-700'
                        }`}>
                          {score}/10
                        </span>
                      )}
                    </div>
                    <ScoreSelector value={score} onChange={(v) => handleScore(aspect.key, v)} />

                    {/* Nota de mejora: visible solo cuando el puntaje es ≤ 5 */}
                    {isOpportunity && (
                      <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2.5">
                        <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-red-700">
                          <MessageSquare size={12} />
                          ¿Cómo podría mejorar este aspecto?
                          <span className="font-normal text-red-500">(opcional)</span>
                        </label>
                        <textarea
                          value={improvementNotes[aspect.key] || ''}
                          onChange={(e) =>
                            setImprovementNotes((prev) => ({ ...prev, [aspect.key]: e.target.value }))
                          }
                          rows={2}
                          placeholder={`Describe brevemente cómo podría mejorar en "${aspect.label}"…`}
                          className="w-full rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:border-red-400 focus:outline-none resize-none"
                        />
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Comentarios generales */}
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Comentarios generales <span className="font-normal text-slate-400">(opcional)</span>
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                  placeholder="¿Qué destacarías de tu experiencia laboral con esta empresa?"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-amber-400 focus:outline-none resize-none"
                />
              </div>

              {!allScored && (
                <p className="text-xs text-amber-600 flex items-center gap-1.5">
                  <AlertCircle size={12} /> Asigna una calificación a todos los aspectos para continuar.
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4 flex-shrink-0">
              <button
                onClick={onClose}
                className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!allScored || submitting || !canEvaluate}
                className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <Star size={15} />}
                Enviar evaluación de {formatPeriodLabel(period)}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
